import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { HandoverRecordRufAssetsService } from "services/handover-record-ruf-assets";
import { getPrintTemplate } from "./components/printTemplate";

const createItem = () => ({
  departmentName: "",
  workContent: "",
  estimatedCost: "",
  actualCost: "",
  inspectionResult: "",
});

const createDateParts = (value = "") => {
  const [day = "", month = "", year = ""] = String(value).split("/");
  const normalizedDay = day.replace(/\D/g, "").slice(0, 2);
  const normalizedMonth = month.replace(/\D/g, "").slice(0, 2);
  return {
    day: normalizedDay ? String(Number(normalizedDay)).padStart(2, "0") : "",
    month: normalizedMonth ? String(Number(normalizedMonth)).padStart(2, "0") : "",
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const createTodayDateParts = () => {
  const now = new Date();
  return {
    day: String(now.getDate()).padStart(2, "0"),
    month: String(now.getMonth() + 1).padStart(2, "0"),
    year: String(now.getFullYear()),
  };
};

const composeDate = (parts) => `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;

const validateDate = (parts, label) => {
  if (!parts.day || !parts.month || !parts.year) {
    throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  }
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  if (year < 1900 || year > 9999 || month < 1 || month > 12) {
    throw new Error(`${label} không hợp lệ`);
  }
  const maxDay = new Date(year, month, 0).getDate();
  if (day < 1 || day > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return composeDate(parts);
};

const formatRowMoney = (item) => ({
  ...item,
  estimatedCost: formatVndIntegerInput(item.estimatedCost),
  actualCost: formatVndIntegerInput(item.actualCost),
});

const lineCellStyle = {
  border: "none",
  borderBottom: "1px dotted #333",
  outline: "none",
  width: "100%",
  background: "transparent",
  fontFamily: `"Times New Roman", Times, serif`,
  fontSize: 15,
};

const moneyCellStyle = {
  ...lineCellStyle,
  textAlign: "right",
};

const thStyle = {
  border: "1px solid #000",
  padding: "6px 4px",
  textAlign: "center",
  fontWeight: 700,
  fontSize: 14,
};

const tdStyle = {
  border: "1px solid #000",
  padding: "4px",
  fontSize: 14,
};

function DateInputField({ label, value, containView, onDateChange, onDateBlur }) {
  return (
    <SoftBox display="flex" alignItems="center" gap={1} sx={{ fontSize: 14 }}>
      <span>{label}</span>
      <input
        disabled={containView}
        style={{ ...lineCellStyle, width: 42, textAlign: "center" }}
        value={value.day}
        onChange={(e) => onDateChange("day", e.target.value)}
        onBlur={() => onDateBlur("day")}
      />
      <span>tháng</span>
      <input
        disabled={containView}
        style={{ ...lineCellStyle, width: 42, textAlign: "center" }}
        value={value.month}
        onChange={(e) => onDateChange("month", e.target.value)}
        onBlur={() => onDateBlur("month")}
      />
      <span>năm</span>
      <input
        disabled={containView}
        style={{ ...lineCellStyle, width: 70, textAlign: "center" }}
        value={value.year}
        onChange={(e) => onDateChange("year", e.target.value)}
      />
    </SoftBox>
  );
}

export default function HandoverRecordRufAssetsDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigate = useNavigate();
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [documentDate, setDocumentDate] = useState(createTodayDateParts());
  const [decisionDate, setDecisionDate] = useState(createTodayDateParts());
  const [workStartDate, setWorkStartDate] = useState(createTodayDateParts());
  const [workEndDate, setWorkEndDate] = useState(createTodayDateParts());

  const [form, setForm] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    debitAccount: "",
    creditAccount: "",
    decisionNumber: "",
    decisionBy: "",
    repairRepresentativeName: "",
    repairRepresentativeTitle: "",
    repairRepresentativeUnit: "",
    assetRepresentativeName: "",
    assetRepresentativeTitle: "",
    assetRepresentativeUnit: "",
    assetNameSpecification: "",
    assetCode: "",
    assetCardNumber: "",
    managementDepartment: "",
    conclusion: "",
  });

  const [items, setItems] = useState([createItem()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await HandoverRecordRufAssetsService.getById(id);
        const data = response.data ? response.data : response;

        setForm({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          debitAccount: data.debitAccount || "",
          creditAccount: data.creditAccount || "",
          decisionNumber: data.decisionNumber || "",
          decisionBy: data.decisionBy || "",
          repairRepresentativeName: data.repairRepresentativeName || "",
          repairRepresentativeTitle: data.repairRepresentativeTitle || "",
          repairRepresentativeUnit: data.repairRepresentativeUnit || "",
          assetRepresentativeName: data.assetRepresentativeName || "",
          assetRepresentativeTitle: data.assetRepresentativeTitle || "",
          assetRepresentativeUnit: data.assetRepresentativeUnit || "",
          assetNameSpecification: data.assetNameSpecification || "",
          assetCode: data.assetCode || "",
          assetCardNumber: data.assetCardNumber || "",
          managementDepartment: data.managementDepartment || "",
          conclusion: data.conclusion || "",
        });

        setDocumentDate(createDateParts(data.documentDate || ""));
        setDecisionDate(createDateParts(data.decisionDate || ""));
        setWorkStartDate(createDateParts(data.workStartDate || ""));
        setWorkEndDate(createDateParts(data.workEndDate || ""));

        setItems((data.items || []).length ? data.items.map(formatRowMoney) : [createItem()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu biên bản bàn giao TSCĐ");
      }
    })();
  }, [id]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (setter, field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setter((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const onDateBlur = (setter, field) => {
    if (field === "year") return;
    setter((prev) => ({ ...prev, [field]: prev[field] ? String(prev[field]).padStart(2, "0") : "" }));
  };

  const setItemField = (index, key, value) => {
    setItems((prev) => {
      const next = [...prev];
      if (key === "estimatedCost" || key === "actualCost") {
        next[index] = { ...next[index], [key]: formatVndIntegerInput(value) };
      } else {
        next[index] = { ...next[index], [key]: value };
      }
      return next;
    });
  };

  const computedItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        estimatedCost: parseVndIntegerInput(item.estimatedCost),
        actualCost: parseVndIntegerInput(item.actualCost),
      })),
    [items]
  );

  const totalEstimatedCost = useMemo(
    () => computedItems.reduce((sum, item) => sum + item.estimatedCost, 0),
    [computedItems]
  );
  const totalActualCost = useMemo(() => computedItems.reduce((sum, item) => sum + item.actualCost, 0), [computedItems]);

  const validate = () => {
    const requiredFields = [
      ["unit", "Đơn vị"],
      ["department", "Bộ phận"],
      ["documentNumber", "Số biên bản"],
      ["decisionNumber", "Số quyết định"],
      ["decisionBy", "Đơn vị/cá nhân ra quyết định"],
      ["assetNameSpecification", "Tên, ký mã hiệu, quy cách TSCĐ"],
    ];
    requiredFields.forEach(([key, label]) => {
      if (!String(form[key] || "").trim()) {
        throw new Error(`${label} không được để trống`);
      }
    });
  };

  const buildPayload = () => {
    validate();
    const normalizedItems = computedItems
      .map((item) => ({
        departmentName: String(item.departmentName || "").trim(),
        workContent: String(item.workContent || "").trim(),
        estimatedCost: item.estimatedCost,
        actualCost: item.actualCost,
        inspectionResult: String(item.inspectionResult || "").trim(),
      }))
      .filter(
        (item) => item.departmentName || item.workContent || item.estimatedCost > 0 || item.actualCost > 0 || item.inspectionResult
      );

    if (!normalizedItems.length) {
      throw new Error("Phải có ít nhất 1 dòng chi tiết hợp lệ");
    }

    return {
      ...form,
      unit: String(form.unit || "").trim(),
      department: String(form.department || "").trim(),
      documentNumber: String(form.documentNumber || "").trim(),
      debitAccount: String(form.debitAccount || "").trim(),
      creditAccount: String(form.creditAccount || "").trim(),
      decisionNumber: String(form.decisionNumber || "").trim(),
      decisionBy: String(form.decisionBy || "").trim(),
      repairRepresentativeName: String(form.repairRepresentativeName || "").trim(),
      repairRepresentativeTitle: String(form.repairRepresentativeTitle || "").trim(),
      repairRepresentativeUnit: String(form.repairRepresentativeUnit || "").trim(),
      assetRepresentativeName: String(form.assetRepresentativeName || "").trim(),
      assetRepresentativeTitle: String(form.assetRepresentativeTitle || "").trim(),
      assetRepresentativeUnit: String(form.assetRepresentativeUnit || "").trim(),
      assetNameSpecification: String(form.assetNameSpecification || "").trim(),
      assetCode: String(form.assetCode || "").trim(),
      assetCardNumber: String(form.assetCardNumber || "").trim(),
      managementDepartment: String(form.managementDepartment || "").trim(),
      conclusion: String(form.conclusion || "").trim(),
      documentDate: validateDate(documentDate, "Ngày biên bản"),
      decisionDate: validateDate(decisionDate, "Ngày quyết định"),
      workStartDate: validateDate(workStartDate, "Ngày bắt đầu sửa chữa"),
      workEndDate: validateDate(workEndDate, "Ngày kết thúc sửa chữa"),
      items: normalizedItems,
      totalEstimatedCost,
      totalActualCost,
    };
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await HandoverRecordRufAssetsService.create(payload);
        toast.success("Tạo biên bản thành công");
      } else {
        await HandoverRecordRufAssetsService.update(id, payload);
        toast.success("Cập nhật biên bản thành công");
      }
      navigate("/templates/handover-record-ruf-assets");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu biên bản thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const payload = buildPayload();
    const win = window.open("", "_blank", "noopener,noreferrer,width=1200,height=900");
    if (!win) {
      toast.error("Không thể mở cửa sổ in");
      return;
    }
    win.document.open();
    win.document.write(getPrintTemplate({ data: payload }));
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox my={3}>
        <Card>
          <SoftBox p={3} sx={{ fontFamily: `"Times New Roman", Times, serif`, fontSize: 14 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <div>
                  <b>Đơn vị:</b>{" "}
                  <input
                    disabled={containView}
                    value={form.unit}
                    onChange={(e) => setField("unit", e.target.value)}
                    style={{ ...lineCellStyle, width: "75%" }}
                  />
                </div>
                <div>
                  <b>Bộ phận:</b>{" "}
                  <input
                    disabled={containView}
                    value={form.department}
                    onChange={(e) => setField("department", e.target.value)}
                    style={{ ...lineCellStyle, width: "74%" }}
                  />
                </div>
              </Grid>
              <Grid item xs={6} textAlign="center">
                <b>Mẫu số: 03 - TSCĐ</b>
                <div>
                  <i>(Kèm theo Thông tư số 99/2025/TT-BTC</i>
                </div>
                <div>
                  <i>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</i>
                </div>
              </Grid>
            </Grid>

            <div style={{ textAlign: "center", marginTop: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 28 / 2 }}>BIÊN BẢN BÀN GIAO TSCĐ</div>
              <div style={{ fontWeight: 700, fontSize: 28 / 2 }}>SỬA CHỮA, BẢO DƯỠNG HOẶC NÂNG CẤP, CẢI TẠO HOÀN THÀNH</div>
            </div>

            <SoftBox mt={1} display="flex" justifyContent="center">
              <DateInputField
                label="Ngày"
                value={documentDate}
                containView={containView}
                onDateChange={(field, inputValue) => setDateField(setDocumentDate, field, inputValue)}
                onDateBlur={(field) => onDateBlur(setDocumentDate, field)}
              />
            </SoftBox>

            <SoftBox mt={1} display="flex" justifyContent="flex-end" gap={2}>
              <div>
                Số:{" "}
                <input
                  disabled={containView}
                  value={form.documentNumber}
                  onChange={(e) => setField("documentNumber", e.target.value)}
                  style={{ ...lineCellStyle, width: 130 }}
                />
              </div>
              <div>
                Nợ:{" "}
                <input
                  disabled={containView}
                  value={form.debitAccount}
                  onChange={(e) => setField("debitAccount", e.target.value)}
                  style={{ ...lineCellStyle, width: 110 }}
                />
              </div>
              <div>
                Có:{" "}
                <input
                  disabled={containView}
                  value={form.creditAccount}
                  onChange={(e) => setField("creditAccount", e.target.value)}
                  style={{ ...lineCellStyle, width: 110 }}
                />
              </div>
            </SoftBox>

            <SoftBox mt={1}>
              Căn cứ Quyết định số:{" "}
              <input
                disabled={containView}
                value={form.decisionNumber}
                onChange={(e) => setField("decisionNumber", e.target.value)}
                style={{ ...lineCellStyle, width: 130 }}
              />{" "}
              ngày{" "}
              <span style={{ display: "inline-block" }}>
                <DateInputField
                  label=""
                  value={decisionDate}
                  containView={containView}
                  onDateChange={(field, inputValue) => setDateField(setDecisionDate, field, inputValue)}
                  onDateBlur={(field) => onDateBlur(setDecisionDate, field)}
                />
              </span>{" "}
              của{" "}
              <input
                disabled={containView}
                value={form.decisionBy}
                onChange={(e) => setField("decisionBy", e.target.value)}
                style={{ ...lineCellStyle, width: 220 }}
              />
            </SoftBox>

            <SoftBox mt={1}>Chúng tôi gồm:</SoftBox>
            <SoftBox mt={0.5}>
              - Ông/Bà{" "}
              <input
                disabled={containView}
                value={form.repairRepresentativeName}
                onChange={(e) => setField("repairRepresentativeName", e.target.value)}
                style={{ ...lineCellStyle, width: 160 }}
              />{" "}
              Chức vụ{" "}
              <input
                disabled={containView}
                value={form.repairRepresentativeTitle}
                onChange={(e) => setField("repairRepresentativeTitle", e.target.value)}
                style={{ ...lineCellStyle, width: 160 }}
              />{" "}
              Đại diện{" "}
              <input
                disabled={containView}
                value={form.repairRepresentativeUnit}
                onChange={(e) => setField("repairRepresentativeUnit", e.target.value)}
                style={{ ...lineCellStyle, width: 220 }}
              />{" "}
              đơn vị sửa chữa, bảo dưỡng hoặc nâng cấp, cải tạo
            </SoftBox>
            <SoftBox mt={0.5}>
              - Ông/Bà{" "}
              <input
                disabled={containView}
                value={form.assetRepresentativeName}
                onChange={(e) => setField("assetRepresentativeName", e.target.value)}
                style={{ ...lineCellStyle, width: 160 }}
              />{" "}
              Chức vụ{" "}
              <input
                disabled={containView}
                value={form.assetRepresentativeTitle}
                onChange={(e) => setField("assetRepresentativeTitle", e.target.value)}
                style={{ ...lineCellStyle, width: 160 }}
              />{" "}
              Đại diện{" "}
              <input
                disabled={containView}
                value={form.assetRepresentativeUnit}
                onChange={(e) => setField("assetRepresentativeUnit", e.target.value)}
                style={{ ...lineCellStyle, width: 220 }}
              />{" "}
              đơn vị có TSCĐ.
            </SoftBox>

            <SoftBox mt={1}>Đã kiểm nhận việc sửa chữa, bảo dưỡng hoặc nâng cấp, cải tạo TSCĐ như sau:</SoftBox>
            <SoftBox mt={0.5}>
              - Tên, ký mã hiệu, quy cách (cấp hạng) TSCĐ{" "}
              <input
                disabled={containView}
                value={form.assetNameSpecification}
                onChange={(e) => setField("assetNameSpecification", e.target.value)}
                style={{ ...lineCellStyle, width: 260 }}
              />
            </SoftBox>
            <SoftBox mt={0.5}>
              - Số hiệu TSCĐ{" "}
              <input
                disabled={containView}
                value={form.assetCode}
                onChange={(e) => setField("assetCode", e.target.value)}
                style={{ ...lineCellStyle, width: 150 }}
              />{" "}
              Số thẻ TSCĐ{" "}
              <input
                disabled={containView}
                value={form.assetCardNumber}
                onChange={(e) => setField("assetCardNumber", e.target.value)}
                style={{ ...lineCellStyle, width: 150 }}
              />
            </SoftBox>
            <SoftBox mt={0.5}>
              - Bộ phận quản lý, sử dụng{" "}
              <input
                disabled={containView}
                value={form.managementDepartment}
                onChange={(e) => setField("managementDepartment", e.target.value)}
                style={{ ...lineCellStyle, width: 360 }}
              />
            </SoftBox>
            <SoftBox mt={0.5} display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              - Thời gian sửa chữa, bảo dưỡng hoặc nâng cấp, cải tạo từ ngày
              <DateInputField
                label=""
                value={workStartDate}
                containView={containView}
                onDateChange={(field, inputValue) => setDateField(setWorkStartDate, field, inputValue)}
                onDateBlur={(field) => onDateBlur(setWorkStartDate, field)}
              />
              đến ngày
              <DateInputField
                label=""
                value={workEndDate}
                containView={containView}
                onDateChange={(field, inputValue) => setDateField(setWorkEndDate, field, inputValue)}
                onDateBlur={(field) => onDateBlur(setWorkEndDate, field)}
              />
            </SoftBox>

            <SoftBox mt={1}>Các bộ phận sửa chữa, bảo dưỡng hoặc nâng cấp, cải tạo gồm có:</SoftBox>
            <div style={{ overflowX: "auto", marginTop: 6 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Tên bộ phận cần sửa chữa, bảo dưỡng, hoặc nâng cấp, cải tạo</th>
                    <th style={thStyle}>Nội dung (mức độ) công việc sửa chữa, bảo dưỡng hoặc nâng cấp, cải tạo</th>
                    <th style={thStyle}>Giá dự toán</th>
                    <th style={thStyle}>Chi phí thực tế</th>
                    <th style={thStyle}>Kết quả kiểm tra sau sửa chữa, bảo dưỡng hoặc nâng cấp, cải tạo</th>
                    {!containView && <th style={thStyle}>#</th>}
                  </tr>
                  <tr>
                    <th style={thStyle}>A</th>
                    <th style={thStyle}>B</th>
                    <th style={thStyle}>1</th>
                    <th style={thStyle}>2</th>
                    <th style={thStyle}>3</th>
                    {!containView && <th style={thStyle}>#</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={`row-${index}`}>
                      <td style={tdStyle}>
                        <input
                          disabled={containView}
                          style={lineCellStyle}
                          value={item.departmentName}
                          onChange={(e) => setItemField(index, "departmentName", e.target.value)}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          disabled={containView}
                          style={lineCellStyle}
                          value={item.workContent}
                          onChange={(e) => setItemField(index, "workContent", e.target.value)}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          disabled={containView}
                          style={moneyCellStyle}
                          value={item.estimatedCost}
                          onChange={(e) => setItemField(index, "estimatedCost", e.target.value)}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          disabled={containView}
                          style={moneyCellStyle}
                          value={item.actualCost}
                          onChange={(e) => setItemField(index, "actualCost", e.target.value)}
                        />
                      </td>
                      <td style={tdStyle}>
                        <input
                          disabled={containView}
                          style={lineCellStyle}
                          value={item.inspectionResult}
                          onChange={(e) => setItemField(index, "inspectionResult", e.target.value)}
                        />
                      </td>
                      {!containView && (
                        <td style={{ ...tdStyle, textAlign: "center", color: "red", fontWeight: 700, cursor: "pointer" }}>
                          <span
                            onClick={() => {
                              if (items.length === 1) return;
                              setItems((prev) => prev.filter((_, i) => i !== index));
                            }}
                          >
                            x
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                  <tr>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: 700 }} colSpan={2}>
                      Cộng
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>
                      {new Intl.NumberFormat("vi-VN").format(totalEstimatedCost)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700 }}>
                      {new Intl.NumberFormat("vi-VN").format(totalActualCost)}
                    </td>
                    <td style={tdStyle} />
                    {!containView && <td style={tdStyle} />}
                  </tr>
                </tbody>
              </table>
            </div>

            {!containView && (
              <SoftBox mt={2} display="flex" gap={1}>
                <SoftButton
                  variant="outlined"
                  color="dark"
                  onClick={() => {
                    setItems((prev) => [...prev, createItem()]);
                  }}
                >
                  THÊM DÒNG
                </SoftButton>
              </SoftBox>
            )}

            <SoftBox mt={1.5}>
              Kết luận:{" "}
              <input
                disabled={containView}
                value={form.conclusion}
                onChange={(e) => setField("conclusion", e.target.value)}
                style={{ ...lineCellStyle, width: "88%" }}
              />
            </SoftBox>

            <SoftBox mt={3} display="flex" justifyContent="space-between">
              <SoftButton variant="outlined" color="secondary" onClick={() => navigate(-1)}>
                Trở về
              </SoftButton>
              <SoftBox display="flex" gap={1}>
                {containView && (
                  <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                    In Bảng
                  </SoftButton>
                )}
                {!containView && (
                  <SoftButton variant="gradient" color="dark" onClick={handleSubmit} disabled={loadingSubmit}>
                    {loadingSubmit ? "Đang lưu..." : id ? "Cập nhật" : "Tạo"}
                  </SoftButton>
                )}
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
    </DashboardLayout>
  );
}
